import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Flex, Text, Button, Spinner, Select, Input, Textarea,
  FormControl, FormLabel, Card, CardBody, CardHeader, Heading, Badge,
  VStack, SimpleGrid, useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { CoverImageUpload } from '../../components/CoverImageUpload';

interface PlaylistDetail {
  id: string;
  name: string;
  description: string | null;
  targetUseCase: string | null;
  thumbnailUrl: string | null;
  isPublished: boolean;
  trackCount: number;
  totalDurationSeconds: number;
  createdAt: string;
  updatedAt: string;
}

interface PlaylistFormProps {
  initialData?: { name: string; description: string; targetUseCase?: string };
  playlistId?: string;
}

const USE_CASE_OPTIONS = [
  'Coffee Shop',
  'Retail',
  'Restaurant',
  'Spa',
  'Gym',
  'Office',
  'Other',
];

function formatTotalDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export function PlaylistForm({ initialData, playlistId: playlistIdProp }: PlaylistFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: routeId } = useParams<{ id: string }>();
  const playlistId = playlistIdProp ?? routeId;
  const isEdit = Boolean(playlistId);

  // Fetch existing playlist data when editing via route param
  const { data: fetchedPlaylist, isLoading: isFetching } = useQuery({
    queryKey: ['admin-playlist', playlistId],
    queryFn: () => apiClient.get<PlaylistDetail>(`/admin/curated-playlists/${playlistId}`),
    enabled: isEdit && !initialData,
  });

  const resolved = initialData ?? (fetchedPlaylist ? {
    name: fetchedPlaylist.name,
    description: fetchedPlaylist.description ?? '',
    targetUseCase: fetchedPlaylist.targetUseCase ?? undefined,
  } : undefined);

  const [name, setName] = useState(resolved?.name ?? '');
  const [description, setDescription] = useState(resolved?.description ?? '');
  const [targetUseCase, setTargetUseCase] = useState(resolved?.targetUseCase ?? '');
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);

  // Populate form fields once data arrives
  useEffect(() => {
    if (resolved) {
      setName(resolved.name);
      setDescription(resolved.description ?? '');
      setTargetUseCase(resolved.targetUseCase ?? '');
    }
    if (fetchedPlaylist?.thumbnailUrl) {
      setCoverImageUrl(fetchedPlaylist.thumbnailUrl);
    }
    // resolved is rebuilt each render; depend on its primitive fields, not its identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved?.name, resolved?.description, resolved?.targetUseCase, fetchedPlaylist?.thumbnailUrl]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) {
        const msg = t('validation.nameRequired', { field: t('admin.playlists.name') });
        setNameError(msg);
        throw new Error(msg);
      }
      setNameError(null);
      const payload = { name, description, targetUseCase: targetUseCase || undefined };
      if (isEdit) {
        return apiClient.patch(`/admin/curated-playlists/${playlistId}`, payload);
      }
      return apiClient.post<{ id: string }>('/admin/curated-playlists', payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlists'] });
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', playlistId] });
      if (!isEdit && data && typeof data === 'object' && 'id' in data) {
        navigate(`/curated-playlists/${(data as { id: string }).id}`);
      } else {
        navigate('/curated-playlists');
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const [isUploadingCover, setIsUploadingCover] = useState(false);

  const handleCoverUpload = useCallback(async (croppedBlob: Blob) => {
    if (!playlistId) return;
    setIsUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, 'cover.png');
      const result = await apiClient.upload<{ url: string }>(
        `/admin/curated-playlists/${playlistId}/cover-image`,
        formData,
      );
      setCoverImageUrl(result.url);
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', playlistId] });
    } finally {
      setIsUploadingCover(false);
    }
  }, [playlistId, queryClient]);

  const handleCoverRemove = useCallback(async () => {
    if (!playlistId) return;
    setIsUploadingCover(true);
    try {
      await apiClient.delete(`/admin/curated-playlists/${playlistId}/cover-image`);
      setCoverImageUrl(null);
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', playlistId] });
    } finally {
      setIsUploadingCover(false);
    }
  }, [playlistId, queryClient]);

  const textColor = useColorModeValue('navy.700', 'white');
  const selectBg = useColorModeValue('secondaryGray.300', 'navy.900');

  if (isEdit && isFetching) {
    return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  }

  // ── Create mode: simple single-card form ──
  if (!isEdit) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="playlist-form">
        {error && <Text color="red.500" mb="16px">{error}</Text>}
        <Card maxW="600px">
          <CardBody>
            <FormControl mb="24px" isInvalid={!!nameError} isRequired>
              <FormLabel ms="10px" fontSize="sm" fontWeight="bold" color={textColor}>
                {t('admin.playlists.name')}
              </FormLabel>
              <Input
                id="playlist-name"
                placeholder={t('admin.playlists.name')}
                value={name}
                onChange={(e) => { setName(e.target.value); setNameError(null); }}
                borderRadius="16px"
                h="44px"
                data-testid="input-name"
              />
              {nameError && <Text color="red.500" fontSize="sm" mt={1} ms="10px">{nameError}</Text>}
            </FormControl>

            <FormControl mb="24px">
              <FormLabel ms="10px" fontSize="sm" fontWeight="bold" color={textColor}>
                {t('admin.playlists.description')}
              </FormLabel>
              <Textarea
                id="playlist-description"
                placeholder={t('admin.playlists.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                borderRadius="16px"
                data-testid="input-description"
              />
            </FormControl>

            <Flex direction="column" mb="24px">
              <Text ms="10px" fontSize="sm" fontWeight="bold" color={textColor} mb="8px">
                {t('admin.playlists.useCase', 'Target Use Case')}
              </Text>
              <Select
                value={targetUseCase}
                onChange={(e) => setTargetUseCase(e.target.value)}
                borderRadius="16px"
                bg={selectBg}
                data-testid="playlist-use-case-select"
                aria-label={t('admin.playlists.selectUseCase', 'Select use case')}
              >
                <option value="">{t('admin.playlists.selectUseCase', 'Select use case')}</option>
                {USE_CASE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </Select>
            </Flex>

            <Button
              colorScheme="brand"
              borderRadius="16px"
              w="100%"
              h="44px"
              mt="8px"
              onClick={() => mutation.mutate()}
              isDisabled={mutation.isPending}
              data-testid="btn-submit"
            >
              {mutation.isPending ? <Spinner size="sm" /> : t('admin.playlists.create')}
            </Button>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // ── Edit mode: header card with cover art + info cards ──
  const playlist = fetchedPlaylist;

  return (
    <VStack spacing={5} align="stretch" pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="playlist-form">
      {/* ── Header Card: Cover Art (editable) + Identity ── */}
      <Card data-testid="edit-header-card">
        <CardBody>
          <Flex gap={6} align="flex-start" direction={{ base: 'column', md: 'row' }}>
            {/* Cover art with inline upload/remove */}
            <Box flexShrink={0}>
              <CoverImageUpload
                currentImageUrl={coverImageUrl}
                onUpload={handleCoverUpload}
                onRemove={handleCoverRemove}
                isUploading={isUploadingCover}
              />
            </Box>

            {/* Playlist identity */}
            <VStack align="flex-start" spacing={2} flex={1} minW={0}>
              <Heading size="lg" noOfLines={2} data-testid="edit-playlist-heading">
                {playlist?.name}
              </Heading>
              {playlist && (
                <>
                  <Badge colorScheme={playlist.isPublished ? 'green' : 'gray'} data-testid="edit-status-badge">
                    {playlist.isPublished ? t('admin.playlists.published') : t('admin.playlists.draft')}
                  </Badge>
                  <Text fontSize="sm" color="gray.500">
                    {playlist.trackCount} {t('admin.playlists.tracks')} · {formatTotalDuration(playlist.totalDurationSeconds)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {t('admin.playlists.createdAt')} {formatDate(playlist.createdAt)}
                  </Text>
                </>
              )}
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Playlist Information Card (editable) ── */}
      <Card data-testid="edit-info-card">
        <CardHeader pb={0}>
          <Heading size="sm">{t('admin.playlists.playlistInfo')}</Heading>
          <Text fontSize="sm" color="gray.500">{t('admin.playlists.playlistInfoDesc')}</Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {error && <Text color="red.500">{error}</Text>}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isInvalid={!!nameError} isRequired>
                <FormLabel>{t('admin.playlists.name')}</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(null); }}
                  data-testid="input-name"
                />
                {nameError && <Text color="red.500" fontSize="sm" mt={1}>{nameError}</Text>}
              </FormControl>
              <FormControl>
                <FormLabel>{t('admin.playlists.useCase')}</FormLabel>
                <Select
                  value={targetUseCase}
                  onChange={(e) => setTargetUseCase(e.target.value)}
                  data-testid="playlist-use-case-select"
                  aria-label={t('admin.playlists.selectUseCase')}
                >
                  <option value="">{t('admin.playlists.selectUseCase')}</option>
                  {USE_CASE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>{t('admin.playlists.description')}</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                data-testid="input-description"
              />
            </FormControl>

            <Flex justify="flex-end">
              <Button
                colorScheme="blue"
                onClick={() => mutation.mutate()}
                isDisabled={mutation.isPending}
                data-testid="btn-submit"
              >
                {mutation.isPending ? <Spinner size="sm" /> : t('common.save')}
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>

      {/* ── Details Card (read-only metadata) ── */}
      {playlist && (
        <Card data-testid="edit-details-card">
          <CardHeader pb={0}>
            <Heading size="sm">{t('admin.playlists.details')}</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('admin.playlists.status')}</Text>
                <Badge colorScheme={playlist.isPublished ? 'green' : 'gray'}>
                  {playlist.isPublished ? t('admin.playlists.published') : t('admin.playlists.draft')}
                </Badge>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('admin.playlists.trackCount')}</Text>
                <Text fontWeight="medium">{playlist.trackCount}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('admin.playlists.totalDuration')}</Text>
                <Text fontWeight="medium">{formatTotalDuration(playlist.totalDurationSeconds)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('admin.playlists.createdAt')}</Text>
                <Text fontWeight="medium">{formatDate(playlist.createdAt)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">{t('admin.playlists.updatedAt')}</Text>
                <Text fontWeight="medium">{formatDate(playlist.updatedAt)}</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}
